import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import { checkPasswordStrength, resetDownloadUserPassword } from "./utils";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [passStrength, setPassStrength] = useState(null);
  const [pwdSuggestion, setPwdSuggestion] = useState(null);
  const [strength, setStrength] = useState({ label: "", score: 0 });
  const [passwordApiResult, setPasswordApiResult] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const updateStrength = (pwd) => {
    if (!pwd) {
      setStrength({ label: "", score: 0 });
      return;
    }
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[!@#$%^&*]/.test(pwd)) score++;
    let label = "Very Weak";
    if (score >= 4) label = "Strong";
    else if (score === 3) label = "Good";
    else if (score === 2) label = "Fair";
    else if (score === 1) label = "Weak";
    setStrength({ label, score });
  };

  const getStrengthColor = () => {
    if (strength.label === "Very Weak" || strength.label === "Weak")
      return "error";
    if (strength.label === "Fair" || strength.label === "Good")
      return "warning";
    if (strength.label === "Strong") return "success";
    return "inherit";
  };

  const resetPasswordState = () => {
    setPassStrength(null);
    setPwdSuggestion(null);
    setStrength({ label: "", score: 0 });
    setPasswordApiResult(null);
  };

  const handlePasswordChange = async (e) => {
    const value = e.target.value;
    setPassword(value);
    setFormErrors((prev) => ({ ...prev, password: undefined }));

    if (!value) {
      resetPasswordState();
      return;
    }

    updateStrength(value);
    clearTimeout(debounceRef.current);

    if (value.length >= 3) {
      debounceRef.current = setTimeout(() => {
        checkPasswordStrength(value, setPassStrength, setPwdSuggestion).then(
          (result) => setPasswordApiResult(result),
        );
      }, 400);
    } else {
      setPassStrength(null);
      setPwdSuggestion(null);
      setPasswordApiResult(null);
    }
  };

  const formValidate = () => {
    const newErrors = {};

    if (!password) {
      newErrors.password = "Please enter a new password";
    } else if (passwordApiResult === null) {
      newErrors.password = "Password has not been validated yet";
    } else if (!passwordApiResult.valid) {
      newErrors.password = passwordApiResult.details
        ? `${passwordApiResult.details}; ${passwordApiResult.message}`
        : passwordApiResult.message ||
          "Password does not meet security requirements";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValidate()) return;

    try {
      setLoading(true);
      setSubmitError(null);
      await resetDownloadUserPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setSubmitError(
        err.message || "An error occurred. The link may have expired.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
          p: 2,
        }}
      >
        <Card sx={{ borderRadius: 3, maxWidth: 480, width: "100%" }}>
          <CardHeader
            title={<font className="layer-title">Invalid Reset Link</font>}
            align="center"
            className="styledtablerow"
          />
          <CardContent>
            <Typography variant="body1" align="center" sx={{ mb: 2 }}>
              This password reset link is missing a required token. Please
              request a new password reset.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
          p: 2,
        }}
      >
        <Card sx={{ borderRadius: 3, maxWidth: 480, width: "100%" }}>
          <CardHeader
            title={
              <font className="layer-title">Password Reset Successfully</font>
            }
            align="center"
            className="styledtablerow"
          />
          <CardContent>
            <Typography variant="body1" align="center" sx={{ mb: 3 }}>
              Password has been updated.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Link to="/About/UserLogin" style={{ textDecoration: "none" }}>
                <Button variant="contained" size="small">
                  Go to Login
                </Button>
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 400,
        p: 2,
      }}
    >
      <Card sx={{ borderRadius: 3, maxWidth: 480, width: "100%" }}>
        <CardHeader
          title={
            <font className="layer-title">Reset Download User Password</font>
          }
          align="center"
          className="styledtablerow"
        />
        <CardContent>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ "& .MuiTextField-root": { width: "100%" }, pt: 2 }}
          >
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}

            <Box sx={{ mb: 2 }}>
              <TextField
                required
                size="small"
                id="reset-new-password"
                label="New Password"
                type="password"
                name="password"
                value={password}
                onChange={handlePasswordChange}
                error={!!formErrors.password}
                helperText={formErrors.password}
                fullWidth
              />

              {(passStrength || strength.score > 0) && (
                <Box sx={{ mt: 1, mb: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(strength.score / 4) * 100}
                    color={getStrengthColor()}
                    sx={{ height: 4, borderRadius: 2, mb: 0.5 }}
                  />
                  <Typography
                    variant="caption"
                    fontSize={10}
                    sx={{ display: "block" }}
                  >
                    Strength: {strength.label}
                  </Typography>
                </Box>
              )}

              {pwdSuggestion && (
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1,
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                    borderRadius: 1,
                    maxHeight: 60,
                    overflow: "auto",
                  }}
                >
                  <Typography
                    variant="caption"
                    fontSize={9}
                    color="textSecondary"
                    sx={{
                      display: "block",
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                    }}
                  >
                    {pwdSuggestion}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                required
                size="small"
                id="reset-confirm-password"
                label="Confirm New Password"
                type="password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setFormErrors((prev) => ({
                    ...prev,
                    confirmPassword: undefined,
                  }));
                }}
                error={!!formErrors.confirmPassword}
                helperText={formErrors.confirmPassword}
                fullWidth
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={loading}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPasswordPage;
